import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import routes from 'routes';
import { getAthletes } from 'api/clubApi';
import {
  exportClubAthletesToFile,
  bulkRemoveAthletes,
  bulkSetAthleteParticipation,
  bulkSetAthleteTeam,
} from 'api/userApi';
import { exportAndDownloadFile } from 'utils/fileDownload';
import {
  groupingBasisOf,
  groupWords,
  formatGroupCell,
} from 'utils/groupLabel';
import {
  activeAgeFilter,
  ageFilterOptions,
  matchesAgeFilter,
} from 'utils/ageFilter';
import {
  NO_TEAM,
  activeTeamFilter,
  matchesTeamFilter,
  teamById,
  teamFilterOptions,
} from 'utils/teamLabel';

import MetaTags from 'components/common/MetaTags';
import AdminTablePageLayout from 'components/admin/AdminTablePageLayout';
import CustomNewButton from 'components/NewDesign/CustomNewButton';
import PinProtectedAction from 'components/common/PinProtected/PinProtectedAction';
import CustomConfirmModal from 'components/common/CustomConfirmModal';
import AthleteActionButtons from 'components/admin/AthleteActionButtons/AthleteActionButtons';

import useBulkSelection from 'hooks/useBulkSelection';
import useTableSort from 'hooks/useTableSort';
import useSessionFilters from 'hooks/useSessionFilters';
import useClubTeams from 'hooks/useClubTeams';
import TeamColourDot from 'components/admin/TeamColourDot';
import BulkActionBar from 'components/admin/BulkActionBar';
import MultiSelectFilter from 'components/admin/MultiSelectFilter';
import BulkOperationResultModal from 'components/admin/BulkOperationResultModal';
import PrintLabelsModal from 'components/admin/PrintLabelsModal';

import styles from 'components/admin/bulkAdminTable.module.css';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'YES', label: 'Active' },
  { value: 'NO', label: 'Inactive' },
];

const GENDER_OPTIONS = [
  { value: 'all', label: 'All genders' },
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
];

const Athletes = () => {
  const { selectedClub } = useSelector((state) => state.user || {});
  const clubId = selectedClub?.clubID || '';

  // The filter, the column and its cells read Age and the group number for an age club, and Year and the year
  // (Y5) for a club that groups by year level. See utils/groupLabel.js.
  const groupingBasis = groupingBasisOf(selectedClub);
  const words = groupWords(groupingBasis);
  const navigate = useNavigate();

  // A club that has teams (a school's houses or factions) gets a column, a filter and a bulk action for
  // them, each named in the club's own word. Each athlete row carries only a team id; the club's short
  // team list is one more request, which the server answers from its cache. A club without teams makes
  // no request and sees none of this. See utils/teamLabel.js.
  const { hasTeams, teams, teamWords } = useClubTeams(clubId);

  const [loading, setLoading] = useState(true);
  const [athletes, setAthletes] = useState([]);

  // Filters — persisted in sessionStorage so they survive navigate-away-and-back.
  const [filters, setFilter] = useSessionFilters(
    `clubAthletesFilters_${clubId}`,
    { searchText: '', age: [], gender: 'all', status: 'all', team: [] }
  );
  const {
    searchText,
    gender: genderFilter,
    status: statusFilter,
  } = filters;

  // Bulk
  const [confirmAction, setConfirmAction] = useState(null); // 'remove' | 'set-active' | 'set-inactive' | 'set-team'
  // The choice in the Set Team dialog: '' until one is made, NO_TEAM for none, otherwise a team id.
  const [chosenTeam, setChosenTeam] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkResultTitle, setBulkResultTitle] = useState('');
  const [showPrintLabels, setShowPrintLabels] = useState(false);

  const fetchAthletes = useCallback(async () => {
    if (!clubId) return;
    try {
      setLoading(true);
      const response = await getAthletes(clubId);
      const enriched = (response.data || []).map((a) => ({
        ...a,
        athleteName: `${a.firstName || ''} ${a.lastName || ''}`.trim(),
        // API returns participationStatus in mixed case ("Yes"/"No"); normalise so
        // filter values and badge logic compare against a single canonical form.
        participationStatus: (a.participationStatus || '').toUpperCase(),
      }));
      setAthletes(enriched);
    } catch (err) {
      console.error('[Athletes] fetch error:', err);
      toast.error('Failed to load athletes');
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  useEffect(() => {
    fetchAthletes();
  }, [fetchAthletes]);

  // Age filter options derived from the loaded data ("All ages" is the empty
  // selection of the multi-select, not an option). Each group the club places
  // by age gets its own option (U5 … U20 for an Under club); its adults are
  // grouped as Senior and Masters. A school has no adults and offers every
  // group singly. See utils/ageFilter, ageFilterOptions.
  const ageOptions = useMemo(
    () =>
      ageFilterOptions(
        athletes.map((a) => a.ageGroup),
        selectedClub
      ),
    [athletes, selectedClub]
  );

  // Ticked age values (empty = all ages), OR'd together — see utils/ageFilter.
  const ageFilter = useMemo(
    () => activeAgeFilter(filters.age, ageOptions),
    [filters.age, ageOptions]
  );

  // Team filter: each of the club's teams, then "No house". Ticked values are OR'd together, like the ages.
  const teamOptions = useMemo(
    () => (hasTeams ? teamFilterOptions(teams, teamWords) : []),
    [hasTeams, teams, teamWords]
  );
  const teamFilter = useMemo(
    () => activeTeamFilter(filters.team, teamOptions),
    [filters.team, teamOptions]
  );

  // Filters
  const filteredAthletes = useMemo(() => {
    return athletes.filter((a) => {
      if (!matchesAgeFilter(a.ageGroup, ageFilter)) return false;
      if (!matchesTeamFilter(a.teamID, teamFilter, teams)) return false;
      if (statusFilter !== 'all' && a.participationStatus !== statusFilter) return false;
      if (genderFilter !== 'all' && a.gender !== genderFilter) return false;
      if (searchText) {
        const q = searchText.toLowerCase();
        if (!a.athleteName.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [athletes, ageFilter, teamFilter, teams, statusFilter, genderFilter, searchText]);

  // Sort
  const sortAccessors = useMemo(
    () => ({
      clubAthleteNumber: (r) => r.clubAthleteNumber || 0,
      athleteName: (r) => `${(r.lastName || '').trim()} ${(r.firstName || '').trim()}`.toLowerCase(),
      ageGroup: (r) => r.ageGroup,
      // By team name; athletes in no team sort together.
      team: (r) => (teamById(teams, r.teamID)?.name || '').toLowerCase(),
      gender: (r) => r.gender,
      participationStatus: (r) => r.participationStatus,
    }),
    [teams]
  );
  const { sortedItems, sortKey, sortDir, setSort } = useTableSort(filteredAthletes, {
    defaultKey: 'athleteName',
    accessors: sortAccessors,
  });

  // Selection
  const idAccessor = useCallback((a) => a.athleteID, []);
  const selection = useBulkSelection(sortedItems, idAccessor);

  const athleteNameById = useMemo(() => {
    const map = {};
    athletes.forEach((a) => {
      map[a.athleteID] = a.athleteName || a.athleteID;
    });
    return map;
  }, [athletes]);

  // Exports the ticked rows, or the rows the filters leave showing when nothing is ticked.
  const handleExportToFile = async () => {
    const athleteIds = selection.selectedOrVisibleIds;
    if (athleteIds.length === 0) {
      toast.info('There are no athletes in the list to export.');
      return;
    }
    await exportAndDownloadFile(
      exportClubAthletesToFile,
      [clubId, athleteIds],
      '',
      'The club athletes list was successfully exported',
      'Fetching club athletes data from the server...'
    );
  };

  const runBulk = async (apiCall, successTitle) => {
    setBulkLoading(true);
    try {
      const response = await apiCall();
      setBulkResult(response.data);
      setBulkResultTitle(successTitle);
      selection.clear();
      await fetchAthletes();
    } catch (err) {
      console.error('[Athletes] bulk error:', err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        'Bulk operation failed';
      toast.error(typeof msg === 'string' ? msg : 'Bulk operation failed');
    } finally {
      setBulkLoading(false);
      setConfirmAction(null);
    }
  };

  const runBulkRemove = () =>
    runBulk(
      () => bulkRemoveAthletes(clubId, selection.selectedIds),
      'Remove Athletes — Results'
    );

  const runBulkSetParticipation = (status) =>
    runBulk(
      () => bulkSetAthleteParticipation(clubId, selection.selectedIds, status),
      status === 'YES'
        ? 'Set Athletes Active — Results'
        : 'Set Athletes Inactive — Results'
    );

  const runBulkSetTeam = () => {
    if (!chosenTeam) {
      toast.info(`Choose a ${teamWords.lower.team} first.`);
      return;
    }
    runBulk(
      () =>
        bulkSetAthleteTeam(
          clubId,
          selection.selectedIds,
          chosenTeam === NO_TEAM ? '' : chosenTeam
        ),
      `Set ${teamWords.team} — Results`
    );
  };

  // Whether every selected athlete is already in the target state — lets us short-circuit the
  // set-active / set-inactive flow client-side (status is already in the row data; no extra API
  // call). Mixed selections proceed; the server treats already-in-state items as no-op successes.
  const selectedSet = new Set(selection.selectedIds);
  const selectedAthletes = athletes.filter((a) => selectedSet.has(a.athleteID));
  const allSelectedActive =
    selectedAthletes.length > 0 &&
    selectedAthletes.every((a) => a.participationStatus === 'YES');
  const allSelectedInactive =
    selectedAthletes.length > 0 &&
    selectedAthletes.every((a) => a.participationStatus === 'NO');

  const sortIndicator = (key) =>
    sortKey === key ? (sortDir === 'asc' ? '▲' : '▼') : '';

  return (
    <>
      <MetaTags title="SplitFast | Athletes" />
      <AdminTablePageLayout loading={loading}>
        <PinProtectedAction clubId={clubId}>
          {({ requestPinValidation }) => {
            const triggerWithPin = (actionType, onApproved, description) =>
              requestPinValidation(actionType, () => onApproved(), null, description);

            const bulkActions = [
              {
                label: 'Print Barcodes',
                variant: 'info',
                tooltip:
                  'Download a PDF of bib-number barcode labels for the selected athletes, formatted for Avery label sheets',
                onClick: () => setShowPrintLabels(true),
              },
              {
                label: 'Set Active',
                variant: 'primary',
                tooltip:
                  'Set participation to Active — the athletes become eligible for meet entry',
                onClick: () => {
                  if (allSelectedActive) {
                    toast.info('All selected athletes are already active.');
                    return;
                  }
                  triggerWithPin(
                    'bulk-set-athletes-active',
                    () => setConfirmAction('set-active'),
                    'The Admin PIN is required to change athlete participation status. The selected athletes will be set to Active, making them eligible for meet entry.'
                  );
                },
              },
              {
                label: 'Set Inactive',
                variant: 'secondary',
                tooltip:
                  'Set participation to Inactive — upcoming meet registrations are removed and the athletes cannot be entered in meets',
                onClick: () => {
                  if (allSelectedInactive) {
                    toast.info('All selected athletes are already inactive.');
                    return;
                  }
                  triggerWithPin(
                    'bulk-set-athletes-inactive',
                    () => setConfirmAction('set-inactive'),
                    "The Admin PIN is required to change athlete participation status. The selected athletes will be set to Inactive — they stay on the roster but cannot be entered in meets, and any registrations for UPCOMING meets are removed (past results are preserved). A family whose athletes are all inactive has its Member contacts set inactive too."
                  );
                },
              },
              // Only for a club that has teams.
              ...(hasTeams
                ? [
                    {
                      label: `Set ${teamWords.team}`,
                      variant: 'primary',
                      tooltip: `Put the selected athletes in one ${teamWords.lower.team}, or in none`,
                      onClick: () =>
                        triggerWithPin(
                          'bulk-set-athletes-team',
                          () => {
                            setChosenTeam('');
                            setConfirmAction('set-team');
                          },
                          `The Admin PIN is required to change which ${teamWords.lower.team} athletes are in. You choose the ${teamWords.lower.team} next.`
                        ),
                    },
                  ]
                : []),
              {
                label: 'Remove',
                variant: 'danger',
                tooltip:
                  'Remove from this club (athlete profile and historical results are preserved)',
                onClick: () =>
                  triggerWithPin(
                    'bulk-remove-athletes',
                    () => setConfirmAction('remove'),
                    "The Admin PIN is required to remove athletes from this club. The athlete profiles and historical race results are preserved, along with their PRs (which are cleaned up at the next season start if the athletes don't rejoin). Their meet registrations and points for this club will be removed."
                  ),
              },
            ];

            return (
              <div className={styles.container}>
                <div className={styles.pageHeading}>
                  <div className={styles.pageTitle}>
                    Athletes
                    <span className={styles.countBadge}>
                      {athletes.length} {athletes.length === 1 ? 'Athlete' : 'Athletes'}
                    </span>
                  </div>
                  <div className={styles.pageActions}>
                    <CustomNewButton
                      variant="info"
                      onClick={handleExportToFile}
                      label="Export to File"
                    />
                    {/* The athlete import takes the file Export to File produces. */}
                    <CustomNewButton
                      variant="info"
                      onClick={() => navigate(routes.admin.members.memberImport)}
                      label="Import from File"
                    />
                  </div>
                </div>

                {/* Filter card */}
                <div className={styles.filterCard}>
                  <div className={`${styles.filterGroup} ${styles.filterGroupSearch}`}>
                    <label className={styles.filterLabel}>Search</label>
                    <input
                      className={styles.filterInput}
                      type="search"
                      placeholder="Search by athlete name…"
                      value={searchText}
                      onChange={(e) => setFilter('searchText', e.target.value)}
                    />
                  </div>
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>{words.value}</label>
                    <MultiSelectFilter
                      value={ageFilter}
                      options={ageOptions}
                      onChange={(next) => setFilter('age', next)}
                      allLabel={`All ${words.lower.values}`}
                      itemLabel={words.lower.value}
                    />
                  </div>
                  {hasTeams && (
                    <div className={styles.filterGroup}>
                      <label className={styles.filterLabel}>{teamWords.team}</label>
                      <MultiSelectFilter
                        value={teamFilter}
                        options={teamOptions}
                        onChange={(next) => setFilter('team', next)}
                        allLabel={`All ${teamWords.lower.teams}`}
                        itemLabel={teamWords.lower.team}
                      />
                    </div>
                  )}
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Gender</label>
                    <select
                      className={styles.filterSelect}
                      value={genderFilter}
                      onChange={(e) => setFilter('gender', e.target.value)}
                    >
                      {GENDER_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Status</label>
                    <select
                      className={styles.filterSelect}
                      value={statusFilter}
                      onChange={(e) => setFilter('status', e.target.value)}
                    >
                      {STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.filterResultCount}>
                    Showing <strong>{filteredAthletes.length}</strong> of {athletes.length}
                  </div>
                </div>

                <BulkActionBar
                  count={selection.count}
                  itemLabel="athlete"
                  actions={bulkActions}
                  onClear={selection.clear}
                  allSelected={selection.allSelected}
                  someSelected={selection.someSelected}
                  onToggleAll={selection.toggleAll}
                />

                <div className={styles.tableCard}>
                  <div className={styles.tableWrapper}>
                    <table className={styles.dataTable}>
                      {/* Name takes what the fixed columns leave of the table (at least
                          1000px wide). With a school's House column these leave it about
                          165px, enough for a first and last name on a line; at 200px for
                          the house and 120px for the ID it had about 85px, and names broke
                          mid-word (Doug, 2026-10-26). */}
                      <colgroup>
                        <col style={{ width: '44px' }} />
                        <col style={{ width: '90px' }} />
                        <col />
                        <col style={{ width: '100px' }} />
                        {hasTeams && <col style={{ width: '160px' }} />}
                        <col style={{ width: '110px' }} />
                        <col style={{ width: '110px' }} />
                        <col style={{ width: '220px' }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th
                            className={`${styles.checkboxCell} ${styles.notSortable}`}
                            aria-label="Select"
                          />

                          <th onClick={() => setSort('clubAthleteNumber')}>
                            ID <span className={styles.sortIndicator}>{sortIndicator('clubAthleteNumber')}</span>
                          </th>
                          <th onClick={() => setSort('athleteName')}>
                            Name <span className={styles.sortIndicator}>{sortIndicator('athleteName')}</span>
                          </th>
                          <th onClick={() => setSort('ageGroup')}>
                            {words.value} <span className={styles.sortIndicator}>{sortIndicator('ageGroup')}</span>
                          </th>
                          {hasTeams && (
                            <th onClick={() => setSort('team')}>
                              {teamWords.team} <span className={styles.sortIndicator}>{sortIndicator('team')}</span>
                            </th>
                          )}
                          <th onClick={() => setSort('gender')}>
                            Gender <span className={styles.sortIndicator}>{sortIndicator('gender')}</span>
                          </th>
                          <th onClick={() => setSort('participationStatus')}>
                            Status <span className={styles.sortIndicator}>{sortIndicator('participationStatus')}</span>
                          </th>
                          <th className={styles.notSortable}>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedItems.length === 0 ? (
                          <tr className={styles.emptyRow}>
                            <td colSpan={hasTeams ? 8 : 7}>
                              {athletes.length === 0
                                ? 'No athletes in this club.'
                                : 'No athletes match the current filters.'}
                            </td>
                          </tr>
                        ) : (
                          sortedItems.map((a) => {
                            const checked = selection.isSelected(a.athleteID);
                            const team = hasTeams ? teamById(teams, a.teamID) : null;
                            return (
                              <tr
                                key={a.athleteID}
                                className={checked ? styles.rowSelected : ''}
                              >
                                <td className={styles.checkboxCell}>
                                  <input
                                    type="checkbox"
                                    aria-label={`Select ${a.athleteName}`}
                                    checked={checked}
                                    onChange={() => selection.toggle(a.athleteID)}
                                  />
                                </td>
                                <td>{a.clubAthleteNumber || 0}</td>
                                <td>{a.athleteName}</td>
                                <td>{formatGroupCell(groupingBasis, a.ageGroup)}</td>
                                {hasTeams && (
                                  <td>
                                    {team && (
                                      <span
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '8px',
                                        }}
                                      >
                                        <TeamColourDot colour={team.colour} />
                                        {team.name}
                                      </span>
                                    )}
                                  </td>
                                )}
                                <td>{a.gender}</td>
                                <td>
                                  <span
                                    className={`${styles.badge} ${a.participationStatus === 'YES' ? styles.badgeYes : styles.badgeNo}`}
                                  >
                                    {a.participationStatus === 'YES' ? 'Active' : 'Inactive'}
                                  </span>
                                </td>
                                <td>
                                  <AthleteActionButtons
                                    clubId={clubId}
                                    athleteId={a.athleteID}
                                  />
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            );
          }}
        </PinProtectedAction>

        {/* Mounted only while open so the form state resets each time. */}
        {showPrintLabels && (
          <PrintLabelsModal
            isOpen={showPrintLabels}
            athletes={selectedAthletes}
            onClose={() => setShowPrintLabels(false)}
          />
        )}

        {confirmAction === 'remove' && (
          <CustomConfirmModal
            show={true}
            title="Remove Athletes from Club"
            content={`Remove ${selection.count} ${selection.count === 1 ? 'athlete' : 'athletes'} from this club? Their race results history is preserved.`}
            confirmLabel="Remove"
            loading={bulkLoading}
            onConfirm={runBulkRemove}
            onCancel={() => setConfirmAction(null)}
          />
        )}

        {(confirmAction === 'set-active' || confirmAction === 'set-inactive') && (
          <CustomConfirmModal
            show={true}
            title={
              confirmAction === 'set-active'
                ? 'Set Athletes Active'
                : 'Set Athletes Inactive'
            }
            content={
              confirmAction === 'set-active'
                ? `Set ${selection.count} ${selection.count === 1 ? 'athlete' : 'athletes'} to Active? Active athletes are eligible for meet entry, and their family contacts are reactivated.`
                : `Set ${selection.count} ${selection.count === 1 ? 'athlete' : 'athletes'} to Inactive? They stay on the roster but cannot be entered in meets, and any registrations for upcoming meets are removed (past results are preserved). Removed registrations are not restored on reactivation.`
            }
            confirmLabel={
              confirmAction === 'set-active' ? 'Set Active' : 'Set Inactive'
            }
            loading={bulkLoading}
            onConfirm={() =>
              runBulkSetParticipation(
                confirmAction === 'set-active' ? 'YES' : 'NO'
              )
            }
            onCancel={() => setConfirmAction(null)}
          />
        )}

        {confirmAction === 'set-team' && (
          <CustomConfirmModal
            show={true}
            title={`Set ${teamWords.team}`}
            content={
              <>
                <p style={{ marginTop: 0 }}>
                  Put {selection.count}{' '}
                  {selection.count === 1 ? 'athlete' : 'athletes'} in:
                </p>
                <select
                  className={styles.filterSelect}
                  style={{ width: '100%' }}
                  value={chosenTeam}
                  onChange={(e) => setChosenTeam(e.target.value)}
                  aria-label={teamWords.team}
                >
                  <option value="">Choose a {teamWords.lower.team}…</option>
                  {teams.map((team) => (
                    <option key={team.teamID} value={team.teamID}>
                      {team.name}
                    </option>
                  ))}
                  <option value={NO_TEAM}>No {teamWords.lower.team}</option>
                </select>
                <p style={{ marginBottom: 0 }}>
                  Each athlete is in one {teamWords.lower.team} at most, so
                  this replaces the {teamWords.lower.team} they are in now.
                </p>
              </>
            }
            confirmLabel={`Set ${teamWords.team}`}
            loading={bulkLoading}
            onConfirm={runBulkSetTeam}
            onCancel={() => setConfirmAction(null)}
          />
        )}

        <BulkOperationResultModal
          show={!!bulkResult}
          title={bulkResultTitle}
          result={bulkResult}
          itemLabel="athlete"
          nameLookup={(id) => athleteNameById[id]}
          onClose={() => setBulkResult(null)}
        />
      </AdminTablePageLayout>
    </>
  );
};

export default Athletes;
